import { useEffect, useState } from "react";
import { COUNTRIES } from "./countries";
import { useModeOptions, useQuoteRoutes, type ModeOption, type ShipMode } from "./modes";
import { readRoutePreference, saveRoutePreference, type RoutePreference } from "./route-preference";

export type RouteSelection = {
  origin: string;
  mode: ShipMode;
  options: ModeOption[];
  served: boolean;
  checking: boolean;
  setOrigin: (origin: string) => void;
  setMode: (mode: ShipMode) => void;
};

function knownOrigin(origin?: string): string {
  return COUNTRIES.some((c) => c.code === origin) ? origin! : "";
}

function startingRoute(requested?: Partial<RoutePreference>): RoutePreference {
  const requestedOrigin = knownOrigin(requested?.origin);
  if (requestedOrigin) return { origin: requestedOrigin, mode: requested?.mode ?? "air" };
  const remembered = readRoutePreference();
  const rememberedOrigin = knownOrigin(remembered?.origin);
  return rememberedOrigin ? { origin: rememberedOrigin, mode: remembered!.mode } : { origin: "", mode: "air" };
}

export function useRouteSelection(requested?: Partial<RoutePreference>): RouteSelection {
  const [start] = useState(() => startingRoute(requested));
  const [origin, setOrigin] = useState(start.origin);
  const [mode, setMode] = useState<ShipMode>(start.mode);
  const { isPending } = useQuoteRoutes();
  const options = useModeOptions(origin);
  const available = options.map((o) => o.mode).join(",");
  const served = options.some((o) => o.mode === mode);

  useEffect(() => {
    const modes = available ? (available.split(",") as ShipMode[]) : [];
    if (modes.length > 0 && !modes.includes(mode)) setMode(modes[0]);
  }, [available, mode]);

  useEffect(() => {
    if (origin && served) saveRoutePreference({ origin, mode });
  }, [origin, mode, served]);

  return { origin, mode, options, served, checking: isPending, setOrigin, setMode };
}
